import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const STORAGE_KEY = (type) => `purchase_chat_session_${type}`;

const OPENER = {
  gold: "Hi — I'll walk you through buying gold safely, step by step. Tell me: which city are you buying in, and is it for jewellery, coins, or bars?",
  silver: "Hi — I'll help you buy silver the right way. Which city, and are you looking at coins/bars or jewellery?",
  land: "Hi — I'll guide you through the agri-land purchase. Tell me: which state / district are you looking in, and roughly how many acres + budget?",
};

export default function PurchaseChat({ type, currentStep, context }) {
  const [sessionId, setSessionId] = useState(() =>
    localStorage.getItem(STORAGE_KEY(type)) || null,
  );
  const [messages, setMessages] = useState([
    { role: "assistant", text: OPENER[type] || OPENER.gold },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Load prior session if we have one
  useEffect(() => {
    if (!sessionId) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/purchase/chat/${sessionId}`);
        if (mounted && Array.isArray(data.messages) && data.messages.length) {
          setMessages(data.messages);
        }
      } catch {
        // fresh session
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    const nextLocal = [...messages, { role: "user", text }];
    setMessages(nextLocal);
    try {
      const { data } = await api.post("/purchase/chat", {
        session_id: sessionId,
        type,
        message: text,
        step_hint: currentStep || null,
        context: context || null,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY(type), data.session_id);
      }
      setMessages([...nextLocal, { role: "assistant", text: data.reply }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Chat unavailable — try again";
      toast.error(msg);
      setMessages(nextLocal);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (sessionId) {
      try {
        await api.delete(`/purchase/chat/${sessionId}`);
      } catch {
        /* ignore */
      }
    }
    localStorage.removeItem(STORAGE_KEY(type));
    setSessionId(null);
    setMessages([{ role: "assistant", text: OPENER[type] || OPENER.gold }]);
    toast.success("Chat reset");
  };

  return (
    <div
      className="border border-line rounded-sm bg-white flex flex-col overflow-hidden"
      data-testid={`purchase-chat-${type}`}
      style={{ minHeight: 380 }}
    >
      <div className="px-4 py-2.5 border-b border-line bg-moss-bg/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-moss" />
          <span className="font-serif-display text-base text-stone_brand-dark font-medium">
            Ask the guide
          </span>
          <span className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light">
            {type} · gpt-5.4
          </span>
        </div>
        <button
          onClick={reset}
          className="text-[0.7rem] text-stone_brand-light hover:text-terracotta flex items-center gap-1 font-mono uppercase"
          data-testid="chat-reset"
          title="Start over"
        >
          <RotateCw className="w-3 h-3" /> Reset
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 max-h-[420px]"
        data-testid="chat-messages"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
          >
            <div
              className={
                "px-3 py-2 rounded-sm text-sm whitespace-pre-wrap max-w-[85%] " +
                (m.role === "user"
                  ? "bg-stone_brand-dark text-bone-50"
                  : "bg-bone-50 border border-line text-stone_brand-dark")
              }
              data-testid={`chat-msg-${m.role}-${i}`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-sm text-sm bg-bone-50 border border-line text-stone_brand-light italic">
              guide is typing…
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-line px-3 py-2.5 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask a question about this step…"
          disabled={sending}
          className="flex-1 h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="chat-input"
        />
        <Button
          onClick={send}
          disabled={sending || !input.trim()}
          className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-10 gap-1.5 px-4"
          data-testid="chat-send"
        >
          <Send className="w-3.5 h-3.5" />
          Send
        </Button>
      </div>
    </div>
  );
}
